import { useEffect, useState, useCallback, memo } from 'react';
import {
  Cpu,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  Monitor,
  HardDrive,
  MemoryStick,
  Play,
  Square,
  ChevronDown,
  ChevronUp,
  Terminal,
  X,
  Wifi,
  WifiOff,
  ShoppingCart,
  Star,
  XOctagon,
  Pencil,
  DollarSign,
  Wallet,
  TrendingUp,
  ExternalLink,
  BanknoteIcon,
} from 'lucide-react';
import {
  agentApi,
  sellerApi,
  GpuAgent,
  ComputeTask,
  AgentListing,
} from '../services/api';
import { useAgentSync } from '../hooks/useAgentSync';
import clsx from 'clsx';

// ─── Status Badge ────────────────────────────────────────

function StatusBadge({ status }: { status: GpuAgent['status'] }) {
  const config = {
    ONLINE: { icon: CheckCircle2, label: 'Online', cls: 'text-green-400 bg-green-400/10' },
    OFFLINE: { icon: XCircle, label: 'Offline', cls: 'text-foreground-muted bg-foreground-muted/10' },
    PENDING: { icon: Clock, label: 'Pending', cls: 'text-yellow-400 bg-yellow-400/10' },
    ERROR: { icon: AlertCircle, label: 'Error', cls: 'text-error bg-error/10' },
  }[status];

  const Icon = config.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium', config.cls)}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// ─── Format helpers ──────────────────────────────────────

function formatBytes(bytes: number) {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${bytes} B`;
}

function timeAgo(dateStr?: string) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

// ─── Sync Indicator ──────────────────────────────────────

function SyncIndicator({ agent, isWsConnected }: { agent: GpuAgent; isWsConnected: boolean }) {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const id = setInterval(() => forceUpdate((n) => n + 1), 5_000);
    return () => clearInterval(id);
  }, []);

  if (agent.status === 'OFFLINE' || agent.status === 'ERROR') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground-muted">
        <WifiOff className="w-3 h-3" />
        Disconnected
      </span>
    );
  }

  if (!agent.lastSeenAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground-muted">
        <Clock className="w-3 h-3" />
        Awaiting sync
      </span>
    );
  }

  const age = Date.now() - new Date(agent.lastSeenAt).getTime();
  const isLive = age < 20_000 && isWsConnected;
  const isStale = age >= 20_000 && age < 60_000;

  if (isLive) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-green-400">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
        </span>
        Live
      </span>
    );
  }

  if (isStale) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-yellow-400">
        <Wifi className="w-3 h-3" />
        Stale ({Math.floor(age / 1000)}s)
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground-muted">
      <WifiOff className="w-3 h-3" />
      Disconnected
    </span>
  );
}

// ─── Register Agent Modal ────────────────────────────────

type RegisterTab = 'pairing' | 'direct';

function RegisterModal({
  isOpen,
  onClose,
  onRegistered,
}: {
  isOpen: boolean;
  onClose: () => void;
  onRegistered: (agent: GpuAgent) => void;
}) {
  const [tab, setTab] = useState<RegisterTab>('pairing');
  const [name, setName] = useState('');
  const [agentUrl, setAgentUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [allowSelfSigned, setAllowSelfSigned] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingExpiresAt, setPairingExpiresAt] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerateCode = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const { code, expiresAt } = await agentApi.generatePairingCode();
      setPairingCode(code);
      setPairingExpiresAt(expiresAt);
    } catch (err: any) {
      setError(err.message || 'Failed to generate pairing code');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyCode = async () => {
    if (!pairingCode) return;
    await navigator.clipboard.writeText(pairingCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDirectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const { agent } = await agentApi.register({ name, agentUrl, apiKey, allowSelfSigned });
      onRegistered(agent);
      setName('');
      setAgentUrl('');
      setApiKey('');
    } catch (err: any) {
      setError(err.message || 'Failed to register agent');
    } finally {
      setIsLoading(false);
    }
  };

  const isExpired = pairingExpiresAt ? Date.now() > pairingExpiresAt : false;

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content p-6 relative max-w-lg">
        <button onClick={onClose} className="absolute top-4 right-4 text-foreground-muted hover:text-foreground">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-foreground mb-1">Register GPU Agent</h2>
        <p className="text-sm text-foreground-secondary mb-5">
          Connect a device running the GPU Agent to sell its compute resources.
        </p>

        {/* Tab switcher */}
        <div className="flex gap-1 bg-background rounded-lg p-1 mb-5 border border-border">
          <button
            onClick={() => { setTab('pairing'); setError(null); }}
            className={clsx(
              'flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors',
              tab === 'pairing'
                ? 'bg-accent/15 text-accent'
                : 'text-foreground-muted hover:text-foreground'
            )}
          >
            Pairing Code
          </button>
          <button
            onClick={() => { setTab('direct'); setError(null); }}
            className={clsx(
              'flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors',
              tab === 'direct'
                ? 'bg-accent/15 text-accent'
                : 'text-foreground-muted hover:text-foreground'
            )}
          >
            Direct URL
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
            {error}
          </div>
        )}

        {/* ── Pairing Code Tab ── */}
        {tab === 'pairing' && (
          <div className="space-y-4">
            <p className="text-sm text-foreground-secondary">
              Generate a one-time pairing code, then enter it in the Platform Agent app on your device.
              No public IP or open ports required.
            </p>

            {!pairingCode ? (
              <button
                onClick={handleGenerateCode}
                disabled={isGenerating}
                className="btn btn-primary w-full"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Generate Pairing Code
              </button>
            ) : (
              <div className="space-y-3">
                <div className="p-4 bg-background rounded-lg border-2 border-accent/30 text-center">
                  <p className="text-xs text-foreground-muted mb-2">Enter this code in Platform Agent</p>
                  <p className="text-3xl font-bold font-mono text-accent tracking-widest select-all">
                    {pairingCode}
                  </p>
                  <p className={clsx(
                    'text-xs mt-2',
                    isExpired ? 'text-error' : 'text-foreground-muted'
                  )}>
                    {isExpired
                      ? 'Code expired — generate a new one'
                      : `Expires in ${Math.max(0, Math.ceil((pairingExpiresAt! - Date.now()) / 60000))} minutes`}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button onClick={handleCopyCode} className="btn btn-secondary flex-1">
                    {copied ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Monitor className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy Code'}
                  </button>
                  <button onClick={handleGenerateCode} disabled={isGenerating} className="btn btn-secondary flex-1">
                    <RefreshCw className={clsx('w-4 h-4', isGenerating && 'animate-spin')} />
                    New Code
                  </button>
                </div>

                <div className="p-3 bg-accent/5 border border-accent/20 rounded-lg text-xs text-foreground-secondary space-y-1">
                  <p className="font-medium text-foreground">How to pair:</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Open the Platform Agent app on your GPU device</li>
                    <li>Sign in with the same account</li>
                    <li>Go to Security & Connectivity and enter the code above</li>
                    <li>The agent will register automatically</li>
                  </ol>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                {pairingCode ? 'Done' : 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* ── Direct URL Tab ── */}
        {tab === 'direct' && (
          <form onSubmit={handleDirectSubmit} className="space-y-4">
            <p className="text-xs text-foreground-muted p-2 bg-background rounded-lg border border-border">
              Requires the agent to have a publicly reachable URL (open port / port forwarding).
              If your device is behind NAT or a firewall, use the Pairing Code method instead.
            </p>

            <div>
              <label htmlFor="agentName" className="label">Agent Name</label>
              <input
                id="agentName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="My Gaming PC"
                required
              />
            </div>

            <div>
              <label htmlFor="agentUrl" className="label">Agent URL</label>
              <input
                id="agentUrl"
                type="url"
                value={agentUrl}
                onChange={(e) => setAgentUrl(e.target.value)}
                className="input"
                placeholder="https://203.0.113.5:7860"
                required
              />
              <p className="text-xs text-foreground-muted mt-1">
                The public URL of the GPU Agent API server shown in the agent's dashboard.
              </p>
            </div>

            <div>
              <label htmlFor="agentApiKey" className="label">API Key</label>
              <input
                id="agentApiKey"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="input font-mono"
                placeholder="gpuagent_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                required
              />
              <p className="text-xs text-foreground-muted mt-1">
                Copy the primary or secondary API key from the agent's settings.
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm text-foreground-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={allowSelfSigned}
                onChange={(e) => setAllowSelfSigned(e.target.checked)}
                className="rounded border-border"
              />
              Allow self-signed TLS certificates
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="btn btn-primary">
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Verify & Register
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Compute Panel ───────────────────────────────────────

function ComputePanel({
  agent,
  onClose,
}: {
  agent: GpuAgent;
  onClose: () => void;
}) {
  const [command, setCommand] = useState('');
  const [args, setArgs] = useState('');
  const [timeout, setTimeout_] = useState('300');
  const [tasks, setTasks] = useState<ComputeTask[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTask, setExpandedTask] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setIsLoadingTasks(true);
    try {
      const { tasks: t } = await agentApi.listTasks(agent.id);
      setTasks(t);
    } catch {
      // agent may be offline
    } finally {
      setIsLoadingTasks(false);
    }
  }, [agent.id]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const parsedArgs = args.trim() ? args.split(/\s+/) : undefined;
      const t = parseInt(timeout, 10);
      await agentApi.submitCompute(agent.id, {
        command,
        args: parsedArgs,
        timeout: isNaN(t) ? undefined : t,
      });
      setCommand('');
      setArgs('');
      await fetchTasks();
    } catch (err: any) {
      setError(err.message || 'Failed to submit task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (taskId: string) => {
    try {
      await agentApi.cancelTask(agent.id, taskId);
      await fetchTasks();
    } catch { /* ok */ }
  };

  const refreshTask = async (taskId: string) => {
    try {
      const { task } = await agentApi.getTask(agent.id, taskId);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? task : t)));
    } catch { /* ok */ }
  };

  const taskStatusColor = (s: ComputeTask['status']) => {
    switch (s) {
      case 'completed': return 'text-green-400';
      case 'running': return 'text-blue-400';
      case 'queued': return 'text-yellow-400';
      case 'failed': return 'text-error';
      case 'cancelled': return 'text-foreground-muted';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground">
          Compute — {agent.name}
        </h3>
        <button onClick={onClose} className="text-foreground-muted hover:text-foreground">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Submit form */}
      <form onSubmit={handleSubmit} className="p-4 bg-background-secondary rounded-lg border border-border space-y-3">
        {error && (
          <div className="p-2 bg-error/10 border border-error/20 rounded text-sm text-error">{error}</div>
        )}
        <div>
          <label className="label">Command</label>
          <input
            type="text"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            className="input font-mono"
            placeholder="python train.py"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Arguments</label>
            <input
              type="text"
              value={args}
              onChange={(e) => setArgs(e.target.value)}
              className="input font-mono"
              placeholder="--epochs 10 --lr 0.001"
            />
          </div>
          <div>
            <label className="label">Timeout (sec)</label>
            <input
              type="number"
              value={timeout}
              onChange={(e) => setTimeout_(e.target.value)}
              className="input"
              min="1"
              max="3600"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={fetchTasks}
            disabled={isLoadingTasks}
            className="btn btn-secondary text-sm"
          >
            <RefreshCw className={clsx('w-3.5 h-3.5', isLoadingTasks && 'animate-spin')} />
            Refresh
          </button>
          <button type="submit" disabled={isSubmitting} className="btn btn-primary text-sm">
            {isSubmitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Play className="w-3.5 h-3.5" />
            )}
            Run
          </button>
        </div>
      </form>

      {/* Task list */}
      {tasks.length === 0 ? (
        <p className="text-sm text-foreground-muted text-center py-8">No tasks yet. Submit a compute job above.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <div key={task.id} className="bg-background-secondary border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-background-hover transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={clsx('text-xs font-medium uppercase', taskStatusColor(task.status))}>
                    {task.status}
                  </span>
                  <code className="text-sm text-foreground truncate">{task.command}</code>
                </div>
                <div className="flex items-center gap-2">
                  {(task.status === 'queued' || task.status === 'running') && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCancel(task.id); }}
                      className="p-1 text-foreground-muted hover:text-error"
                      title="Cancel"
                    >
                      <Square className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(task.status === 'running' || task.status === 'queued') && (
                    <button
                      onClick={(e) => { e.stopPropagation(); refreshTask(task.id); }}
                      className="p-1 text-foreground-muted hover:text-foreground"
                      title="Refresh"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {expandedTask === task.id ? (
                    <ChevronUp className="w-4 h-4 text-foreground-muted" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-foreground-muted" />
                  )}
                </div>
              </button>
              {expandedTask === task.id && (
                <div className="px-4 pb-4 border-t border-border space-y-3 pt-3">
                  <div className="grid grid-cols-3 gap-4 text-xs text-foreground-secondary">
                    <div>
                      <span className="text-foreground-muted">Submitted</span>
                      <p>{new Date(task.submittedAt).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-foreground-muted">Exit Code</span>
                      <p>{task.exitCode !== null ? task.exitCode : '—'}</p>
                    </div>
                    <div>
                      <span className="text-foreground-muted">Duration</span>
                      <p>
                        {task.startedAt && task.completedAt
                          ? `${((task.completedAt - task.startedAt) / 1000).toFixed(1)}s`
                          : task.startedAt
                          ? 'Running...'
                          : '—'}
                      </p>
                    </div>
                  </div>
                  {task.stdout && (
                    <div>
                      <p className="text-xs text-foreground-muted mb-1">stdout</p>
                      <pre className="bg-background p-3 rounded text-xs text-foreground font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                        {task.stdout}
                      </pre>
                    </div>
                  )}
                  {task.stderr && (
                    <div>
                      <p className="text-xs text-foreground-muted mb-1">stderr</p>
                      <pre className="bg-background p-3 rounded text-xs text-error/80 font-mono overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                        {task.stderr}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Regions ─────────────────────────────────────────────

const REGIONS = [
  'US-East', 'US-West', 'US-Central',
  'EU-West', 'EU-Central', 'EU-North',
  'Asia-East', 'Asia-Southeast', 'Asia-South',
  'South America', 'Africa', 'Oceania', 'Middle East',
];

// ─── List Agent Modal ────────────────────────────────────

function ListAgentModal({
  isOpen,
  agent,
  existingListing,
  onClose,
  onDone,
}: {
  isOpen: boolean;
  agent: GpuAgent;
  existingListing?: AgentListing | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(existingListing?.title || agent.name);
  const [description, setDescription] = useState('');
  const [pricePerHour, setPricePerHour] = useState(existingListing?.pricePerHour?.toString() || '0.50');
  const [region, setRegion] = useState(existingListing?.region || '');
  const [country, setCountry] = useState(existingListing?.country || '');
  const [tags, setTags] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!existingListing?.isActive;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const data = {
        title: title.trim(),
        description: description.trim(),
        pricePerHour: parseFloat(pricePerHour),
        region,
        country: country.trim(),
        tags: tags.trim() ? tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      };

      if (isEditing) {
        await agentApi.updateListing(agent.id, data);
      } else {
        await agentApi.createListing(agent.id, data);
      }
      onDone();
    } catch (err: any) {
      setError(err.message || 'Failed to list agent');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content p-6 relative max-w-lg">
        <button onClick={onClose} className="absolute top-4 right-4 text-foreground-muted hover:text-foreground">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-foreground mb-1">
          {isEditing ? 'Edit Marketplace Listing' : 'List on Marketplace'}
        </h2>
        <p className="text-sm text-foreground-secondary mb-5">
          {isEditing
            ? 'Update your marketplace listing details.'
            : `List "${agent.name}" on the marketplace so others can rent your GPU compute.`}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="listTitle" className="label">Listing Title</label>
            <input
              id="listTitle"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input"
              placeholder="RTX 4090 - High Performance GPU"
              required
            />
          </div>

          <div>
            <label htmlFor="listDesc" className="label">Description</label>
            <textarea
              id="listDesc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input resize-none"
              rows={3}
              placeholder="Describe your GPU setup, performance, uptime guarantees..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="listPrice" className="label">Price per Hour (USD)</label>
              <input
                id="listPrice"
                type="number"
                value={pricePerHour}
                onChange={(e) => setPricePerHour(e.target.value)}
                className="input"
                placeholder="0.50"
                min="0.01"
                step="0.01"
                required
              />
            </div>
            <div>
              <label htmlFor="listRegion" className="label">Region</label>
              <select
                id="listRegion"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="input"
                required
              >
                <option value="">Select region...</option>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="listCountry" className="label">Country</label>
              <input
                id="listCountry"
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="input"
                placeholder="United States"
                required
              />
            </div>
            <div>
              <label htmlFor="listTags" className="label">Tags (comma-separated)</label>
              <input
                id="listTags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="input"
                placeholder="ML, training, inference"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="btn btn-primary">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditing ? 'Updating...' : 'Listing...'}
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4" />
                  {isEditing ? 'Update Listing' : 'List on Marketplace'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Agent Card ──────────────────────────────────────────

const AgentCard = memo(function AgentCard({
  agent,
  onRefresh,
  onRemove,
  onOpenCompute,
  onOpenListing,
  isWsConnected,
}: {
  agent: GpuAgent;
  onRefresh: () => void;
  onRemove: () => void;
  onOpenCompute: () => void;
  onOpenListing: () => void;
  isWsConnected: boolean;
}) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await agentApi.verify(agent.id);
      onRefresh();
    } catch { /* ok */ }
    setIsRefreshing(false);
  };

  const handleRemove = async () => {
    if (!confirm(`Remove agent "${agent.name}"? This cannot be undone.`)) return;
    setIsRemoving(true);
    try {
      await agentApi.remove(agent.id);
      onRemove();
    } catch { /* ok */ }
    setIsRemoving(false);
  };

  const hw = agent.hardwareInfo;
  const gpu = hw?.gpus?.[0];
  const rl = agent.resourceLock;

  return (
    <div className="bg-background-secondary border border-border rounded-lg p-5">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-foreground">{agent.name}</h3>
            <StatusBadge status={agent.status} />
          </div>
          <p className="text-xs text-foreground-muted truncate max-w-xs" title={agent.agentUrl}>
            {agent.agentUrl}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1.5 text-foreground-muted hover:text-foreground rounded hover:bg-background-hover"
            title="Verify & refresh"
          >
            <RefreshCw className={clsx('w-4 h-4', isRefreshing && 'animate-spin')} />
          </button>
          <button
            onClick={handleRemove}
            disabled={isRemoving}
            className="p-1.5 text-foreground-muted hover:text-error rounded hover:bg-error/10"
            title="Remove agent"
          >
            {isRemoving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Hardware info */}
      {hw && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {gpu && (
            <div className="flex items-start gap-2">
              <Monitor className="w-4 h-4 text-accent mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{gpu.model}</p>
                <p className="text-xs text-foreground-muted">
                  {formatBytes(gpu.vram)} VRAM
                  {gpu.temperatureGpu != null && ` · ${gpu.temperatureGpu}°C`}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-start gap-2">
            <Cpu className="w-4 h-4 text-accent mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{hw.cpu.brand}</p>
              <p className="text-xs text-foreground-muted">
                {hw.cpu.cores} cores · {hw.cpu.speed} GHz
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MemoryStick className="w-4 h-4 text-accent mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">Memory</p>
              <p className="text-xs text-foreground-muted">
                {formatBytes(hw.memory.total)} total · {formatBytes(hw.memory.free)} free
              </p>
            </div>
          </div>
          {hw.disk && (
            <div className="flex items-start gap-2">
              <HardDrive className="w-4 h-4 text-accent mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Disk</p>
                <p className="text-xs text-foreground-muted">
                  {formatBytes(hw.disk.total)} total · {formatBytes(hw.disk.free)} free
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Resource lock */}
      {rl?.locked && (
        <div className="mb-4 p-3 bg-accent/5 border border-accent/20 rounded-lg">
          <p className="text-xs font-medium text-accent mb-1">Locked Resources</p>
          <div className="flex gap-4 text-xs text-foreground-secondary">
            <span>GPU {rl.gpuPercent}%</span>
            <span>CPU {rl.cpuCores} cores</span>
            <span>RAM {rl.ramGb} GB</span>
          </div>
        </div>
      )}

      {/* Reliability */}
      {agent.reliabilityInfo && (
        <div className="mb-4 text-xs text-foreground-secondary">
          Reliability:{' '}
          <span
            className={clsx(
              'font-medium',
              agent.reliabilityInfo.overallState === 'reliable' && 'text-green-400',
              agent.reliabilityInfo.overallState === 'degraded' && 'text-yellow-400',
              agent.reliabilityInfo.overallState === 'unreliable' && 'text-error'
            )}
          >
            {agent.reliabilityInfo.overallState}
          </span>
        </div>
      )}

      {/* Marketplace listing info */}
      {agent.listing?.isActive && (
        <div className="mb-4 p-3 bg-green-400/5 border border-green-400/20 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-400">
              <ShoppingCart className="w-3 h-3" />
              Listed on Marketplace
            </span>
            <span className="text-xs font-semibold text-accent">
              ${agent.listing.pricePerHour.toFixed(2)}/hr
            </span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-foreground-muted">
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-400" />
              {agent.listing.avgRating.toFixed(1)} ({agent.listing.reviewCount} reviews)
            </span>
            <span>{agent.listing.region}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={onOpenListing}
              className="text-[11px] text-accent hover:underline flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" />
              Edit
            </button>
            <button
              onClick={async () => {
                if (!confirm('Remove this listing from the marketplace?')) return;
                try {
                  await agentApi.removeListing(agent.id);
                  onRefresh();
                } catch { /* ok */ }
              }}
              className="text-[11px] text-error hover:underline flex items-center gap-1"
            >
              <XOctagon className="w-3 h-3" />
              Delist
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-3">
          <SyncIndicator agent={agent} isWsConnected={isWsConnected} />
          <span className="text-xs text-foreground-muted">
            Last seen: {timeAgo(agent.lastSeenAt)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!agent.listing?.isActive && (
            <button
              onClick={onOpenListing}
              disabled={agent.status !== 'ONLINE'}
              className="btn btn-secondary text-xs py-1.5 px-3"
              title={agent.status !== 'ONLINE' ? 'Agent must be online to list' : ''}
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              List
            </button>
          )}
          <button
            onClick={onOpenCompute}
            disabled={agent.status !== 'ONLINE'}
            className="btn btn-primary text-xs py-1.5 px-3"
          >
            <Terminal className="w-3.5 h-3.5" />
            Send Compute
          </button>
        </div>
      </div>
    </div>
  );
});

// ─── Main Page ───────────────────────────────────────────

// ─── Seller Earnings Dashboard ─────────────────────────────
function EarningsPanel() {
  const [profile, setProfile] = useState<any>(null);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [earningsHistory, setEarningsHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const data = await sellerApi.getProfile();
      setProfile(data.profile);
      setPayouts(data.payouts || []);
      if (data.hasProfile) {
        const hist = await sellerApi.getEarningsHistory(12);
        setEarningsHistory(hist.history);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOnboard = async () => {
    setOnboarding(true);
    try {
      const { url } = await sellerApi.onboard();
      setOnboardingUrl(url);
      window.open(url, '_blank');
    } catch { /* ignore */ }
    setOnboarding(false);
  };

  const handleCompleteOnboarding = async () => {
    try {
      await sellerApi.completeOnboarding();
      fetchData();
    } catch { /* ignore */ }
  };

  if (loading) return <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-foreground-muted" /></div>;

  if (!profile) {
    return (
      <div className="card p-6 border-accent/20">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-5 h-5 text-accent" />
          <h3 className="font-semibold text-foreground">Seller Earnings</h3>
        </div>
        <p className="text-sm text-foreground-secondary mb-4">
          List a GPU on the marketplace to start earning. Once you have active listings with rentals, your earnings will appear here.
        </p>
        <button onClick={handleOnboard} disabled={onboarding} className="btn btn-primary text-sm">
          {onboarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <BanknoteIcon className="w-4 h-4" />}
          Set Up Payouts
        </button>
      </div>
    );
  }

  return (
    <div className="card p-6 border-green-500/20">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="w-5 h-5 text-green-500" />
        <h3 className="font-semibold text-foreground">Seller Earnings</h3>
        {!profile.stripeOnboardingComplete && (
          <span className="ml-auto text-xs text-warning">Payout setup incomplete</span>
        )}
      </div>

      {/* This Month Breakdown */}
      <div className="p-4 bg-background-secondary rounded-lg border border-border mb-4">
        <h4 className="text-xs font-semibold text-foreground-tertiary uppercase tracking-wide mb-3">This Month</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-foreground-secondary">Gross Revenue (buyer paid)</span>
            <span className="font-mono text-foreground">${profile.currentMonthGrossRevenue.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-foreground-secondary">Platform Fee (15%)</span>
            <span className="font-mono text-red-400">-${profile.currentMonthPlatformFee.toFixed(2)}</span>
          </div>
          <div className="border-t border-border pt-2 flex justify-between text-sm font-semibold">
            <span className="text-foreground">Your Net Earnings</span>
            <span className="font-mono text-green-400">${profile.currentMonthEarnings.toFixed(2)}</span>
          </div>
          <p className="text-[10px] text-foreground-muted mt-2">
            GST is collected from the buyer separately and does not reduce your earnings.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="p-3 bg-background-secondary rounded-lg">
          <p className="text-xs text-foreground-tertiary">Pending Payout</p>
          <p className="text-lg font-bold text-green-400">${profile.pendingEarnings.toFixed(2)}</p>
        </div>
        <div className="p-3 bg-background-secondary rounded-lg">
          <p className="text-xs text-foreground-tertiary">All-Time Earned (net)</p>
          <p className="text-lg font-bold text-foreground">${profile.totalEarnings.toFixed(2)}</p>
        </div>
        <div className="p-3 bg-background-secondary rounded-lg">
          <p className="text-xs text-foreground-tertiary">Last Payout</p>
          <p className="text-lg font-bold text-foreground-secondary">
            {profile.lastPayoutAt ? new Date(profile.lastPayoutAt).toLocaleDateString() : 'None yet'}
          </p>
        </div>
      </div>

      {/* Bank / Payout status */}
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-4 p-3 bg-background-secondary rounded-lg text-sm">
        <div className="flex items-center gap-2">
          {profile.bankAccountVerified ? (
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          ) : (
            <AlertCircle className="w-4 h-4 text-warning" />
          )}
          <span className="text-foreground">
            Bank: {profile.bankAccountVerified ? 'Verified' : 'Not verified'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {profile.stripeOnboardingComplete ? (
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          ) : (
            <Clock className="w-4 h-4 text-warning" />
          )}
          <span className="text-foreground">
            Stripe: {profile.stripeOnboardingComplete ? 'Connected' : 'Incomplete'}
          </span>
        </div>
        {!profile.stripeOnboardingComplete && (
          <button onClick={handleOnboard} disabled={onboarding} className="btn btn-secondary text-xs ml-auto">
            {onboarding ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
            Complete Setup
          </button>
        )}
        {profile.stripeOnboardingComplete && !profile.bankAccountVerified && (
          <button onClick={handleCompleteOnboarding} className="btn btn-secondary text-xs ml-auto">
            <RefreshCw className="w-3 h-3" />
            Verify
          </button>
        )}
      </div>

      {/* Payout History */}
      {payouts.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Payout History</h4>
          <div className="space-y-2">
            {payouts.map((p: any) => (
              <div key={p.id} className="p-3 bg-background-secondary rounded-lg text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-foreground font-medium">{p.billingPeriod}</span>
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded text-[10px] font-medium',
                    p.status === 'PAID' ? 'bg-green-500/10 text-green-400' :
                    p.status === 'PENDING' ? 'bg-yellow-500/10 text-yellow-400' :
                    p.status === 'FAILED' ? 'bg-red-500/10 text-red-400' :
                    'bg-blue-500/10 text-blue-400'
                  )}>{p.status}</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-tertiary">
                  <span>Gross ${p.grossEarnings.toFixed(2)}</span>
                  <span className="text-foreground-muted">→</span>
                  <span className="text-red-400/70">Fee ${p.platformFee.toFixed(2)} (15%)</span>
                  <span className="text-foreground-muted">→</span>
                  <span className="font-mono font-bold text-green-400">Net ${p.netPayout.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Earnings Chart (simple bar list) */}
      {earningsHistory.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Monthly Earnings</h4>
          <div className="space-y-1">
            {earningsHistory.filter(e => e.netEarnings > 0).slice(0, 6).map((e: any) => {
              const max = Math.max(...earningsHistory.map((h: any) => h.grossRevenue), 1);
              return (
                <div key={e.billingPeriod} className="flex items-center gap-2 text-xs">
                  <span className="text-foreground-tertiary w-16">{e.billingPeriod}</span>
                  <div className="flex-1 h-4 bg-background-secondary rounded overflow-hidden">
                    <div
                      className="h-full bg-green-500/30 rounded"
                      style={{ width: `${(e.grossRevenue / max) * 100}%` }}
                    />
                  </div>
                  <span className="font-mono text-foreground w-20 text-right">${e.netEarnings.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function SellGpuPage() {
  const [agents, setAgents] = useState<GpuAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [computeAgentId, setComputeAgentId] = useState<string | null>(null);
  const [listingAgentId, setListingAgentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [releasedBanner, setReleasedBanner] = useState<string | null>(null);

  const fetchAgents = useCallback(async () => {
    try {
      const { agents: a } = await agentApi.list();
      setAgents(a);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleAgentUpdate = useCallback((updated: GpuAgent) => {
    setAgents((prev) => {
      const idx = prev.findIndex((a) => a.id === updated.id);
      if (idx === -1) return [updated, ...prev];
      const next = [...prev];
      next[idx] = { ...prev[idx], ...updated };
      return next;
    });
  }, []);

  const handleAgentOffline = useCallback((agentId: string) => {
    setAgents((prev) =>
      prev.map((a) => (a.id === agentId ? { ...a, status: 'OFFLINE' as const } : a))
    );
  }, []);

  const handleRentalReleased = useCallback((_rentalId: string, _listingId: string, reason: string) => {
    fetchAgents();
    const msg = reason === 'prepaid_hours_exhausted'
      ? 'A rental has ended — prepaid hours exhausted. Your GPU is now available.'
      : 'A rental has ended. Your GPU is now available.';
    setReleasedBanner(msg);
    setTimeout(() => setReleasedBanner(null), 8000);
  }, [fetchAgents]);

  const { isConnected: isWsConnected } = useAgentSync({
    onAgentUpdate: handleAgentUpdate,
    onAgentOffline: handleAgentOffline,
    onRentalReleased: handleRentalReleased,
  });

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Slow fallback poll (60s) in case WebSocket is down
  useEffect(() => {
    const interval = setInterval(fetchAgents, 60_000);
    return () => clearInterval(interval);
  }, [fetchAgents]);

  const computeAgent = computeAgentId ? agents.find((a) => a.id === computeAgentId) : null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Sell GPU</h1>
          <p className="text-foreground-secondary mt-1 text-sm">
            Register GPU Agent devices to sell compute resources on the network.
          </p>
        </div>
        <button onClick={() => setShowRegister(true)} className="btn btn-primary flex-shrink-0">
          <Plus className="w-4 h-4" />
          Register Agent
        </button>
      </div>

      {/* Earnings Dashboard */}
      <EarningsPanel />

      {releasedBanner && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          {releasedBanner}
          <button onClick={() => setReleasedBanner(null)} className="ml-auto hover:text-green-300">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {error && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">{error}</div>
      )}

      {/* Compute panel (shown when a user clicks "Send Compute" on an agent) */}
      {computeAgent && (
        <ComputePanel agent={computeAgent} onClose={() => setComputeAgentId(null)} />
      )}

      {/* Agents grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-foreground-muted" />
        </div>
      ) : agents.length === 0 ? (
        <div className="text-center py-20 space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto">
            <Cpu className="w-8 h-8 text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">No agents registered</h3>
            <p className="text-foreground-secondary text-sm mt-1 max-w-md mx-auto">
              Install the GPU Agent on a device with a GPU, then register it here
              using its URL and API key to start selling compute.
            </p>
          </div>
          <button onClick={() => setShowRegister(true)} className="btn btn-primary mx-auto">
            <Plus className="w-4 h-4" />
            Register Your First Agent
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onRefresh={fetchAgents}
              onRemove={fetchAgents}
              onOpenCompute={() => setComputeAgentId(agent.id)}
              onOpenListing={() => setListingAgentId(agent.id)}
              isWsConnected={isWsConnected}
            />
          ))}
        </div>
      )}

      {/* Register modal */}
      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onRegistered={(agent) => {
          setAgents((prev) => [agent, ...prev]);
          setShowRegister(false);
        }}
      />

      {/* Listing modal */}
      {listingAgentId && (() => {
        const listingAgent = agents.find((a) => a.id === listingAgentId);
        if (!listingAgent) return null;
        return (
          <ListAgentModal
            isOpen={true}
            agent={listingAgent}
            existingListing={listingAgent.listing}
            onClose={() => setListingAgentId(null)}
            onDone={() => {
              setListingAgentId(null);
              fetchAgents();
            }}
          />
        );
      })()}
    </div>
  );
}
